import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { User, Package, Heart, Settings, LogOut, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fake order data
  const orders = [
    {
      id: 'ORD-2024-001',
      date: '2024-03-15',
      total: '$1,250.00',
      status: 'Delivered',
      items: [
        { name: 'Diamond Pendant', quantity: 1, price: '$850.00' },
        { name: 'Pearl Earrings', quantity: 1, price: '$400.00' }
      ]
    },
    {
      id: 'ORD-2024-002',
      date: '2024-03-10',
      total: '$2,800.00',
      status: 'Processing',
      items: [
        { name: 'Gold Chain', quantity: 1, price: '$1,200.00' },
        { name: 'Sapphire Ring', quantity: 1, price: '$1,600.00' }
      ]
    },
    {
      id: 'ORD-2024-003',
      date: '2024-03-05',
      total: '$950.00',
      status: 'Shipped',
      items: [
        { name: 'Silver Bracelet', quantity: 1, price: '$450.00' },
        { name: 'Crystal Necklace', quantity: 1, price: '$500.00' }
      ]
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'Processing':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'Shipped':
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-24">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-gold/10 to-gold/5 p-8">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center">
                  <User className="w-10 h-10 text-gold" />
                </div>
                <div>
                  <h1 className="text-2xl font-serif text-gray-900">{user?.name}</h1>
                  <p className="text-gray-600">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Account Navigation */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-8">
              <div className="md:col-span-1">
                <nav className="space-y-2">
                  <button className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                    <User className="w-5 h-5" />
                    <span>Profile</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                    <Package className="w-5 h-5" />
                    <span>Orders</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                    <Heart className="w-5 h-5" />
                    <span>Wishlist</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                    <Settings className="w-5 h-5" />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </nav>
              </div>

              {/* Main Content */}
              <div className="md:col-span-3">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h2 className="text-xl font-serif text-gray-900 mb-4">Recent Orders</h2>
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="bg-white p-6 rounded-lg shadow-sm">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-sm text-gray-600">Order {order.id}</p>
                            <p className="text-sm text-gray-600">Placed on {order.date}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(order.status)}
                            <span className="text-sm font-medium">{order.status}</span>
                          </div>
                        </div>
                        <div className="border-t border-gray-100 pt-4">
                          {order.items.map((item, index) => (
                            <div key={index} className="flex justify-between text-sm mb-2">
                              <span>{item.name} × {item.quantity}</span>
                              <span>{item.price}</span>
                            </div>
                          ))}
                          <div className="flex justify-between font-medium mt-4 pt-4 border-t border-gray-100">
                            <span>Total</span>
                            <span>{order.total}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage; 